import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../ui/Button';
import Wordmark from '../ui/Wordmark';
import { primaryNavigation, primaryCta } from '../../utils/navigation';
import { ROUTES } from '../../utils/routes';
import businessInfo, { getEmailHref } from '../../utils/businessInfo';

/** Focusable elements inside the drawer, in tab order, for focus containment. */
const FOCUSABLE = 'a[href], button:not([disabled])';

/**
 * The mobile navigation drawer.
 *
 * Rendered only while open: an unmounted panel cannot be tabbed into, cannot
 * be announced, and needs no `aria-hidden` bookkeeping. Opening animates
 * through a keyframe on mount rather than a CSS transition, which is what makes
 * conditional rendering and motion compatible.
 *
 * While it is open it takes three things seriously -- Escape closes it, the
 * page behind it does not scroll, and Tab cycles within the panel instead of
 * wandering into the page underneath.
 *
 * Like the header it mirrors, it withholds the call to action on the assistant
 * page (see Nav.jsx): the drawer is the only way around the site there, and a
 * competing ask in it would be the one thing the page does not need.
 */
const MobileNav = ({ isOpen, onClose }) => {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus moves into the drawer so a keyboard user is inside the open panel,
    // and the first Tab goes to the next control in it.
    closeRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const emailHref = getEmailHref();
  const showCta = pathname !== ROUTES.home;

  return (
    <div className="mobile-nav">
      <div
        className="mobile-nav__scrim"
        role="presentation"
        onClick={() => onClose()}
      />

      <div
        className="mobile-nav__panel"
        id="mobile-navigation"
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        ref={panelRef}
      >
        <div className="mobile-nav__top">
          <Link to={ROUTES.home} className="mobile-nav__brand" onClick={() => onClose({ returnFocus: false })}>
            <Wordmark size={22} />
          </Link>
          <button
            type="button"
            className="mobile-nav__close"
            ref={closeRef}
            onClick={() => onClose()}
          >
            <span className="visually-hidden">Close site menu</span>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <nav aria-label="Mobile">
          <ul className="mobile-nav__list">
            {primaryNavigation.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="mobile-nav__link"
                  onClick={() => onClose({ returnFocus: false })}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {showCta ? (
          <Button to={primaryCta.to} variant="primary" size="md" fullWidth>
            {primaryCta.label}
          </Button>
        ) : null}

        {emailHref ? (
          <a className="mobile-nav__email" href={emailHref}>
            {businessInfo.email}
          </a>
        ) : (
          <span className="mobile-nav__email">{businessInfo.email}</span>
        )}
      </div>
    </div>
  );
};

export default MobileNav;
